import React, { useState } from 'react';

import { Box, Button, DropdownMenu, Text, VStack } from '@betterment/samba-react';

import { AccountRow } from './AccountRow';
import { HouseholdAccountsQuery } from './graphql/query.householdAccounts.generated';
import { HouseholdAccountYtdReturnsQuery } from './graphql/query.householdAccountYtdReturns.generated';
import { HeaderCell } from './HeaderCell';

type HouseholdData = Extract<HouseholdAccountsQuery['household'], { __typename: 'Household' }>;
type EnvelopeAccount = HouseholdData['accounts'][number];
type HouseholdAccountYtd = Extract<HouseholdAccountYtdReturnsQuery['household'], { __typename: 'Household' }>;
type YtdReturnsData = HouseholdAccountYtd['accountYtdReturns'];
type ReturnType = 'twr' | 'mwr';

export const AccountsTable: React.FC<{
  accounts: EnvelopeAccount[];
  ytdReturnsData?: YtdReturnsData;
  ytdReturnsLoading?: boolean;
  showYtdReturns: boolean;
  isWeightedReturnsEnabled: boolean;
}> = ({ accounts, ytdReturnsData, ytdReturnsLoading, showYtdReturns, isWeightedReturnsEnabled }) => {
  const [returnType, setReturnType] = useState<ReturnType>('twr');

  const gridColumns = showYtdReturns
    ? '120px minmax(172px, 1fr) minmax(172px, 1fr) minmax(auto, 200px) minmax(auto, 200px) minmax(auto, 36px)'
    : '120px minmax(172px, 1fr) minmax(172px, 1fr) minmax(auto, 200px) minmax(auto, 36px)';

  const tableHeaderStyling = {
    display: 'flex',
    alignItems: 'center',

    '& span': {
      display: 'flex',
      alignItems: 'center',
      minHeight: 'size.300',
    },
  };

  return (
    <Box
      css={{
        width: '100%',
        display: 'grid',
        gridTemplateColumns: gridColumns,
        gridAutoRows: 'auto',
        alignItems: 'end',
      }}
    >
      <HeaderCell css={{ gridColumn: '1', ...tableHeaderStyling }}>
        <Text variant="text50" color="text.weak">
          Client(s)
        </Text>
      </HeaderCell>
      <HeaderCell css={{ gridColumn: '2', ...tableHeaderStyling }}>
        <Text variant="text50" color="text.weak">
          Account
        </Text>
      </HeaderCell>
      <HeaderCell css={{ gridColumn: '3', ...tableHeaderStyling }}>
        <Text variant="text50" color="text.weak">
          Portfolio
        </Text>
      </HeaderCell>
      {showYtdReturns && (
        <HeaderCell css={{ gridColumn: '4', justifyContent: 'end', textAlign: 'right', ...tableHeaderStyling }}>
          {isWeightedReturnsEnabled ? (
            <DropdownMenu>
              <DropdownMenu.Trigger asChild={true}>
                <Button
                  variant="textPrimary"
                  css={{
                    display: 'inline-flex',
                    fontSize: 'fontSize.50',
                    alignItems: 'center',
                    verticalAlign: 'middle',
                    color: 'text.weak',
                    _hover: { color: 'text.weak' },
                  }}
                >
                  {returnType === 'twr' ? 'Time-weighted return (YTD)' : 'Money-weighted return (YTD)'}
                  <Button.Icon name="CaretDown" css={{ minWidth: 'size.300' }} />
                </Button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Content css={{ minWidth: '320px' }} align="end">
                <DropdownMenu.Item onSelect={() => setReturnType('twr')} css={{ cursor: 'pointer' }}>
                  <VStack>
                    <Text>Time-weighted return</Text>
                    <Text variant="text50" color="text.weak">
                      Year-to-date
                    </Text>
                  </VStack>
                </DropdownMenu.Item>
                <DropdownMenu.Item onSelect={() => setReturnType('mwr')} css={{ cursor: 'pointer' }}>
                  <VStack>
                    <Text>Money-weighted return</Text>
                    <Text variant="text50" color="text.weak">
                      Year-to-date
                    </Text>
                  </VStack>
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu>
          ) : (
            <Text variant="text50" color="text.weak">
              Total return YTD
            </Text>
          )}
        </HeaderCell>
      )}
      <HeaderCell
        css={{
          gridColumn: showYtdReturns ? '5' : '4',
          justifyContent: 'end',
          paddingLeft: 'space.100',
          paddingRight: 'space.300',
          textAlign: 'right',
          ...tableHeaderStyling,
        }}
      >
        <Text variant="text50" color="text.weak">
          {isWeightedReturnsEnabled ? 'Balance (Earnings YTD)' : 'Balance'}
        </Text>
      </HeaderCell>
      {accounts.map(account => (
        <AccountRow
          key={account.id}
          account={account}
          ytdReturnsData={ytdReturnsData}
          ytdReturnsLoading={ytdReturnsLoading}
          showYtdReturns={showYtdReturns}
          returnType={returnType}
          isWeightedReturnsEnabled={isWeightedReturnsEnabled}
        />
      ))}
    </Box>
  );
};
